/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

const QUERY_KEYS = ["home", "homepage", "homepageVersion"];
const RESET_VALUES = new Set(["reset", "default", "clear"]);

function readRequestedVersion() {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  for (const key of QUERY_KEYS) {
    const value = params.get(key);
    if (value) {
      return value.trim();
    }
  }

  return null;
}

export default function useHomepageVersion({ defaultVersionId, versions }) {
  const versionIds = useMemo(() => new Set(Object.keys(versions)), [versions]);

  const resolveVersion = useCallback(() => {
    const requestedVersion = readRequestedVersion();
    const requestedLower = requestedVersion?.toLowerCase();

    if (requestedLower && RESET_VALUES.has(requestedLower)) {
      return { source: "default", versionId: defaultVersionId };
    }

    if (requestedVersion && versionIds.has(requestedVersion)) {
      return { source: "url", versionId: requestedVersion };
    }

    return { source: "default", versionId: defaultVersionId };
  }, [defaultVersionId, versionIds]);

  const [selection, setSelection] = useState(resolveVersion);

  useEffect(() => {
    setSelection(resolveVersion());
  }, [resolveVersion]);

  const setVersion = useCallback(
    (nextVersionId) => {
      if (!versionIds.has(nextVersionId)) {
        return false;
      }

      setSelection({ source: "manual", versionId: nextVersionId });
      return true;
    },
    [versionIds],
  );

  const clearVersion = useCallback(() => {
    setSelection({ source: "default", versionId: defaultVersionId });
  }, [defaultVersionId]);

  return {
    clearVersion,
    setVersion,
    source: selection.source,
    versionId: versionIds.has(selection.versionId) ? selection.versionId : defaultVersionId,
  };
}
